import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import Textarea from "../../../../componentes/input/textarea";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import FuncionarioService from "../../../../services/resource/funcionarioService";
import FilialService from "../../../../services/resource/filialService";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";
import { SETOR_OPTIONS } from "../../../../utils/solicitacoes/setores";
import { useCargoGerencial } from "../../../../hooks/solicitacoes/useCargoGerencial";

const funcionarioService = new FuncionarioService();
const filialService = new FilialService();

const MUDA_SETOR_OPTIONS = [
    { value: true, label: "Sim" },
    { value: false, label: "Não" },
];

const inicial = {
    unidadeId: null,
    funcionarioId: null,
    incluiPromocao: false,
    incluiTransferencia: false,
    incluiEnquadramento: false,
    cargoAtual: "",
    cargoProposto: "",
    salarioAtual: "",
    salarioProposto: "",
    dataPrevistaMudanca: "",
    setorAtual: "",
    mudaDeSetor: false,
    setorProposto: "",
    filialPropostaId: null,
    escalaAtual: "",
    escalaProposta: "",
    beneficios: "",
    justificativa: "",
};

// Processo único que reúne Promoção (cargo/salário), Transferência (filial) e Enquadramento
// (escala/turno) — o usuário marca qualquer combinação das 3 (ao menos uma), e cada seção só
// aparece/exige seus campos quando marcada. Mesma técnica condicional que "Muda de setor" já usava.
function CheckboxTipoMudanca({ label, checked, onChange }) {
    return (
        <label className="flex items-center gap-2.5 px-3 py-2 border border-input rounded-md text-sm font-medium text-foreground/80 cursor-pointer hover:bg-muted">
            <input
                type="checkbox"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/15"
            />
            {label}
        </label>
    );
}

export default function NovaSolicitacaoPromocaoForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar }) {
    const [form, setForm] = useState(inicial);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [funcionarios, setFuncionarios] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const { carregando: carregandoCargo, autorizado: autorizadoPorCargo, filial: filialSolicitante } = useCargoGerencial();

    useEffect(() => {
        filialService.listarAtivas().then(res => setUnidades(res.data)).catch(() => setUnidades([]));
    }, []);

    // Pré-preenche a filial com a do usuário logado assim que a consulta ao perfil retornar (a
    // busca é assíncrona, então isso não pode ficar num efeito com array de dependências vazio —
    // ele rodaria antes do valor chegar). Só preenche se o campo ainda estiver vazio, pra não
    // sobrescrever uma escolha manual feita enquanto a consulta ainda estava em andamento.
    useEffect(() => {
        if (!filialSolicitante) return;
        setForm(prev => (prev.unidadeId ? prev : { ...prev, unidadeId: filialSolicitante }));
    }, [filialSolicitante]);

    // Funcionário é sempre filtrado pela filial escolhida — ao trocar a filial, a lista é
    // recarregada e a seleção anterior (de outra filial) é descartada.
    useEffect(() => {
        if (!form.unidadeId) {
            setFuncionarios([]);
            return;
        }
        funcionarioService.listar({ filialId: form.unidadeId })
            .then(res => setFuncionarios((res.data || []).filter(f => f.ativo)))
            .catch(() => setFuncionarios([]));
    }, [form.unidadeId]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function selecionarUnidade(unidadeId) {
        setForm(prev => ({ ...prev, unidadeId, funcionarioId: null, cargoAtual: "" }));
    }

    function selecionarFuncionario(funcionarioId) {
        const funcionario = funcionarios.find(f => f.id === funcionarioId);
        setForm(prev => ({
            ...prev,
            funcionarioId,
            cargoAtual: funcionario?.cargo || prev.cargoAtual,
        }));
    }

    function validar() {
        if (!form.unidadeId || !form.funcionarioId) {
            toast.error("Selecione a filial e o funcionário.");
            return false;
        }
        if (!form.incluiPromocao && !form.incluiTransferencia && !form.incluiEnquadramento) {
            toast.error("Selecione ao menos um tipo de mudança: promoção, transferência ou enquadramento.");
            return false;
        }
        if ((form.incluiPromocao || form.incluiEnquadramento) && (!form.cargoAtual.trim() || !form.cargoProposto.trim())) {
            toast.error("Informe o cargo atual e o cargo proposto.");
            return false;
        }
        if (form.incluiPromocao) {
            if (!form.salarioAtual || !form.salarioProposto || !form.dataPrevistaMudanca || !form.setorAtual.trim()) {
                toast.error("Preencha todos os campos obrigatórios da promoção.");
                return false;
            }
            if (form.mudaDeSetor && !form.setorProposto.trim()) {
                toast.error("Informe o setor proposto, já que houve mudança de setor.");
                return false;
            }
        }
        if (form.incluiTransferencia && !form.filialPropostaId) {
            toast.error("Selecione a filial de destino da transferência.");
            return false;
        }
        if (form.incluiEnquadramento) {
            if (!form.escalaAtual.trim() || !form.escalaProposta.trim()) {
                toast.error("Preencha todos os campos obrigatórios do enquadramento.");
                return false;
            }
        }
        if (!form.justificativa.trim()) {
            toast.error("A justificativa é obrigatória.");
            return false;
        }
        if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("ETAPA_2_APROVACAO_GESTOR");
        } catch {
            return;
        }

        setEnviando(true);
        service.criarSolicitacao({
            ...form,
            usuarioEscolhidoId,
            salarioAtual: form.incluiPromocao && form.salarioAtual ? Number(form.salarioAtual) : null,
            salarioProposto: form.incluiPromocao && form.salarioProposto ? Number(form.salarioProposto) : null,
            setorProposto: form.incluiPromocao && form.mudaDeSetor ? form.setorProposto : null,
        })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, anexos),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao criar solicitação", error))
            .finally(() => setEnviando(false));
    }

    if (carregandoCargo) {
        return (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                <i className="fa fa-spinner fa-spin mr-2" /> Verificando permissão...
            </div>
        );
    }

    if (!autorizadoPorCargo) {
        return (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
                <i className="fa fa-lock text-muted-foreground text-xl" />
                <p className="text-sm text-foreground font-medium">Você não tem permissão para iniciar esta solicitação.</p>
                <p className="text-xs text-muted-foreground">Apenas Supervisores, Coordenadores ou Gerentes podem abrir este processo.</p>
            </div>
        );
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
        >
            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1.5">Filial <span className="text-destructive">*</span></label>
                <CustomDropdown
                    value={form.unidadeId}
                    onChange={e => selecionarUnidade(e.value)}
                    options={unidades}
                    optionValue="id"
                    optionLabel="nome"
                    placeholder="Selecione"
                    filter
                />
            </div>

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1.5">Funcionário <span className="text-destructive">*</span></label>
                <CustomDropdown
                    value={form.funcionarioId}
                    onChange={e => selecionarFuncionario(e.value)}
                    options={funcionarios}
                    optionValue="id"
                    optionLabel={f => f.cargo ? `${f.nome} — ${f.cargo}` : f.nome}
                    placeholder={form.unidadeId ? "Pesquisar funcionário" : "Selecione a filial primeiro"}
                    disabled={!form.unidadeId}
                    filter
                />
            </div>

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1.5">
                    Tipo de mudança <span className="text-destructive">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <CheckboxTipoMudanca label="Promoção (cargo/salário)" checked={form.incluiPromocao} onChange={v => set("incluiPromocao", v)} />
                    <CheckboxTipoMudanca label="Transferência (filial)" checked={form.incluiTransferencia} onChange={v => set("incluiTransferencia", v)} />
                    <CheckboxTipoMudanca label="Enquadramento (escala/turno)" checked={form.incluiEnquadramento} onChange={v => set("incluiEnquadramento", v)} />
                </div>
            </div>

            {(form.incluiPromocao || form.incluiEnquadramento) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Cargo atual" required value={form.cargoAtual} onChange={e => set("cargoAtual", e.target.value)} />
                    <Input label="Cargo proposto" required value={form.cargoProposto} onChange={e => set("cargoProposto", e.target.value)} />
                </div>
            )}

            {form.incluiPromocao && (
                <div className="flex flex-col gap-4 border-l-2 border-primary/30 pl-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Input type="number" min="0" step="0.01" label="Salário atual" required value={form.salarioAtual} onChange={e => set("salarioAtual", e.target.value)} />
                        <Input type="number" min="0" step="0.01" label="Salário proposto" required value={form.salarioProposto} onChange={e => set("salarioProposto", e.target.value)} />
                        <Input type="date" label="Data prevista para mudança" required value={form.dataPrevistaMudanca} onChange={e => set("dataPrevistaMudanca", e.target.value)} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <CustomDropdown label="Setor atual" required value={form.setorAtual} onChange={e => set("setorAtual", e.value)} options={SETOR_OPTIONS} placeholder="Selecione" />
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Muda de setor <span className="text-destructive">*</span></label>
                            <CustomDropdown
                                value={form.mudaDeSetor}
                                onChange={e => set("mudaDeSetor", e.value)}
                                options={MUDA_SETOR_OPTIONS}
                            />
                        </div>
                        {form.mudaDeSetor && (
                            <CustomDropdown label="Setor proposto" required value={form.setorProposto} onChange={e => set("setorProposto", e.value)} options={SETOR_OPTIONS} placeholder="Selecione" />
                        )}
                    </div>
                </div>
            )}

            {form.incluiTransferencia && (
                <div className="flex flex-col gap-4 border-l-2 border-primary/30 pl-4">
                    <div>
                        <label className="text-sm font-medium text-foreground/80 block mb-1.5">Filial de destino <span className="text-destructive">*</span></label>
                        <CustomDropdown
                            value={form.filialPropostaId}
                            onChange={e => set("filialPropostaId", e.value)}
                            options={unidades}
                            optionValue="id"
                            optionLabel="nome"
                            placeholder="Selecione"
                            filter
                        />
                    </div>
                </div>
            )}

            {form.incluiEnquadramento && (
                <div className="flex flex-col gap-4 border-l-2 border-primary/30 pl-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input label="Escala atual" required value={form.escalaAtual} onChange={e => set("escalaAtual", e.target.value)} />
                        <Input label="Escala proposta" required value={form.escalaProposta} onChange={e => set("escalaProposta", e.target.value)} />
                    </div>
                </div>
            )}

            <Textarea label="Benefícios para o colaborador" value={form.beneficios} onChange={e => set("beneficios", e.target.value)} rows={2}
                placeholder="Ex: Plano de saúde, Vale alimentação, Auxílio combustível, Bônus..." />

            <Textarea label="Justificativa" required value={form.justificativa} onChange={e => set("justificativa", e.target.value)} rows={3} />

            <div className="flex justify-end">
                <Button variant="blue" label="Solicitar" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
